import { Component, Input, ElementRef, ViewChild, forwardRef, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { getTextWidth } from '@helpers/get-text-width';

const INLINE_EDIT_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => InlineEditComponent),
  multi: true
};

type InlineEditStatus = 'editing' | 'expanded' | 'displayText';

@Component({
  selector: 'app-inline-edit',
  templateUrl: './inline-edit.component.html',
  styleUrls: ['./inline-edit.component.scss'],
  providers: [INLINE_EDIT_CONTROL_VALUE_ACCESSOR]
})
export class InlineEditComponent implements ControlValueAccessor {
  @ViewChild('inlineEditControl', { static: false }) inlineEditControl: ElementRef; // input DOM element
  @ViewChild('textElement', { static: false }) textElement: ElementRef; // input DOM element
  @Input() label = ''; // Label value for input element
  @Input() type = 'text'; // The type of input element
  @Input() required = false; // Is input requried?
  @Input() disabled = false; // Is input disabled?
  @Input() classList = '';
  @Input() messageForValidationFailure = ''; // message to display if does not pass validation
  private _value = ''; // Private variable for input value
  public preValue = ''; // The value before clicking to edit
  private _editing = false; // Is Component in edit mode?
  public onChange: any = Function.prototype; // Trascend the onChange event
  public onTouched: any = Function.prototype; // Trascend the onTouch event
  public expanded = false;
  public status: InlineEditStatus = 'displayText';

  public get editing() {
    return this.status === 'editing';
  }

  public set editing(value) {
    this._editing = value;
    if (this._editing) this.status = 'editing';
    else this.setDisplayText();
  }

  public setDisplayText() {
    if (!this.isExpanded) this.status = 'displayText';
    else this.status = 'expanded';
  }

  public get ellipsisApplied(): boolean {
    return (
      this.textElement &&
      getTextWidth(this.value, this.textElement.nativeElement as HTMLElement) >=
        this.textElement.nativeElement.clientWidth
    );
  }

  public get isExpanded(): boolean {
    return this.status === 'expanded';
  }

  // Control Value Accessors for ngModel
  public get value(): string {
    return this._value;
  }

  public set value(v: string) {
    if (v !== this._value) {
      this._value = v;
      this.onChange(v);
    }
  }

  constructor(element: ElementRef, private _renderer: Renderer2) {}

  // Required for ControlValueAccessor interface
  public writeValue(value: any): void {
    this._value = value;
  }

  // Required forControlValueAccessor interface
  public registerOnChange(fn: (_: any) => {}): void {
    this.onChange = fn;
  }

  // Required forControlValueAccessor interface
  public registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  // Do stuff when the input element loses focus
  public onBlur(): void {
    this.editing = false;
  }

  // Do stuff when the user presses enter while editing
  public onEnter(): void {
    this.editing = false;
  }

  // Start the editing process for the input element
  public edit(): void {
    if (this.disabled) return;

    this.preValue = this.value;
    this.editing = true; // ! the setter includes (this.status = 'editing') inside

    // Focus on the input element just as the editing begins
    setTimeout(() => this.inlineEditControl.nativeElement.focus());
  }

  public onFieldClick() {
    // If is an expandable text (with ellipsis applied), expand It at first click
    if (this.ellipsisApplied && !this.isExpanded) this.status = 'expanded';
    // If is a short text, edit It directly
    else if (this.isExpanded || !this.ellipsisApplied) this.edit();
    // Else
    else this.status = 'displayText';
  }
}
